<template>
  <PmDataModalStockTypeServicePure
    :id="id"
    :is-loading="isLoading"
    :name="stockTypeService?.caption"
    :number="stockTypeService?.number"
    :type="stockTypeService?.stockTypeServiceType?.caption"
    :start-date="parseServerDateString(stockTypeService?.startDate)"
    :end-date="parseServerDateString(stockTypeService?.endDate)"
    :stock-type="stockType"
    :created-date="parseServerDateString(stockTypeService?.createdAt)"
    :created-by="stockTypeService?.createdBy?.displayName"
    :edited-date="parseServerDateString(stockTypeService?.updatedAt)"
    :edited-by="stockTypeService?.updatedBy?.displayName"
    :resources="resources"
    @open-resource="openResource"
    @open-resource-allocation="openResourceAllocation"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'

import { DATA_MODAL_TYPE } from '@/constants/persoplan'
import { EVENT } from '@/constants/events'
import { parseServerDateString } from '@/utilities/date'
import EventHub from '@/eventHub'

import PmDataModalStockTypeServicePure, {
  type Props as PropsDataModalStockTypeServicePure,
} from '@/components/persoplan/DataModal/DataModalStockTypeService/PmDataModalStockTypeServicePure.vue'

import { DataModalStockTypeServiceDocument } from '../../../../../generated/graphql'

export interface Props {
  id: number
}

const props = defineProps<Props>()

const stockTypeServiceQuery = useQuery(
  DataModalStockTypeServiceDocument,
  () => ({
    id: props.id,
  }),
  () => ({
    // Don't update the cache, which might change the calendar view.
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })
)

const stockTypeService = computed(
  () => stockTypeServiceQuery.result.value?.stockTypeService
)

const isLoading = computed(() => {
  return stockTypeServiceQuery.loading.value === true
})

const stockType = computed(() => {
  if (!stockTypeService.value?.stockType) return

  const result: PropsDataModalStockTypeServicePure['stockType'] = {
    id: stockTypeService.value.stockType.id,
    number: stockTypeService.value.stockType.number,
    caption: stockTypeService.value.stockType.caption,
  }

  return result
})

const resources = computed(() => {
  const result: PropsDataModalStockTypeServicePure['resources'] = []

  stockTypeService.value?.resourceFunctionAllocations?.forEach(
    (resourceFunctionAllocation) => {
      if (!resourceFunctionAllocation) return

      result.push({
        id: resourceFunctionAllocation.id,
        title: resourceFunctionAllocation.resourceFunction?.caption,
        abbreviation: resourceFunctionAllocation.resourceFunction?.abbreviation,
        startDate: parseServerDateString(resourceFunctionAllocation.startDate),
        endDate: parseServerDateString(resourceFunctionAllocation.endDate),
        resourceAllocations:
          resourceFunctionAllocation.resourceAllocations?.flatMap(
            (resourceAllocation) => {
              if (!resourceAllocation) return []

              const title =
                resourceAllocation.address?.displayName ??
                resourceAllocation.vehicle?.caption

              if (!title) return []

              return [{ id: resourceAllocation.id, title }]
            }
          ) ?? [],
      })
    }
  )

  return result
})

function openResource(id: number) {
  EventHub.$emit(EVENT.DATA_MODAL_SHOW, {
    type: DATA_MODAL_TYPE.RESOURCE,
    id,
  })
}

function openResourceAllocation(id: number) {
  EventHub.$emit(EVENT.DATA_MODAL_SHOW, {
    type: DATA_MODAL_TYPE.RESOURCE_ALLOCATION,
    id,
  })
}
</script>
